import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { handleAuthError } from '../services/api/authUtils';

const WorkspaceContext = createContext();

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const { accessToken, setTenantContext } = useAuth();
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  
  const [isTenantContextReady, setIsTenantContextReady] = useState(false);
  
  // Track which workspace IDs have been hydrated to prevent infinite loops
  const hydratedWorkspaceIds = React.useRef(new Set());
  
  const [workspaces, setWorkspaces] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load workspace from localStorage on mount only
  useEffect(() => {
    const savedWorkspace = localStorage.getItem('selectedWorkspace');
    if (savedWorkspace) {
      try {
        const workspace = JSON.parse(savedWorkspace);
        // Set the current workspace immediately
        setCurrentWorkspace(workspace);
      } catch (error) {
        localStorage.removeItem('selectedWorkspace');
      }
    }
  }, []); // Remove accessToken dependency to prevent infinite loop

  // Separate effect to handle tenant context restoration when accessToken becomes available
  useEffect(() => {
    const savedWorkspace = localStorage.getItem('selectedWorkspace');
    
    if (accessToken && savedWorkspace && !isTenantContextReady) {
      try {
        const workspace = JSON.parse(savedWorkspace);

        // Restore tenant context through the API so stale tenant tokens cannot mark the workspace ready.
        setTenantContext(workspace.id, workspace.environment)
          .then(result => {
            if (result.success) {
              setIsTenantContextReady(true);
            } else {
              // Tenant context must be valid before tenant-scoped API calls are allowed.
              setCurrentWorkspace(null);
              localStorage.removeItem('selectedWorkspace');
              localStorage.removeItem('tenantAccessToken');
              setIsTenantContextReady(false);
            }
          })
          .catch(error => {
            setCurrentWorkspace(null);
            localStorage.removeItem('selectedWorkspace');
            localStorage.removeItem('tenantAccessToken');
            setIsTenantContextReady(false);
          });
      } catch (error) {
        // Error parsing saved workspace for tenant context restoration
      }
    }
  }, [accessToken, isTenantContextReady, setTenantContext]);

  // Fetch workspaces from API (memoized)
  const fetchWorkspaces = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const response = await fetch(`${API_BASE_URL}/api/workspaces`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Check if it's a 401 error and handle it globally
        if (response.status === 401) {
          const error = { response: { status: 401 } };
          if (handleAuthError(error)) {
            throw new Error('Session expired - redirected to login');
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setWorkspaces(data.workspaces);
      } else {
        throw new Error(data.error || 'Failed to fetch workspaces');
      }
    } catch (error) {
      // Don't set error state for session expired errors (they redirect to login)
      if (!error.message.includes('Session expired')) {
        setError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  // Fetch workspaces from API when accessToken is available
  useEffect(() => {
    if (accessToken) {
      fetchWorkspaces();
    }
  }, [accessToken, fetchWorkspaces]);

  // Ensure we have the full workspace (with integrations/timezone) as soon as possible
  useEffect(() => {
    const ensureFullWorkspace = async () => {
      try {
        const ws = currentWorkspace;
        if (!ws?.id) return;
        
        // Skip if already hydrated for this workspace ID
        if (hydratedWorkspaceIds.current.has(ws.id)) {
          // Check if hydration is still needed
          if (ws?.integrations && ws?.timezone) {
            return; // Already hydrated, no need to fetch again
          }
          // If marked as hydrated but still missing data, remove from set to retry
          hydratedWorkspaceIds.current.delete(ws.id);
        }
        
        const needsHydration = !ws?.integrations || !ws?.timezone;
        if (!needsHydration) {
          hydratedWorkspaceIds.current.add(ws.id);
          return;
        }
        
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        const token = localStorage.getItem('accessToken') || localStorage.getItem('tenantAccessToken');
        if (!token) return;
        
        const resp = await fetch(`${API_BASE_URL}/api/workspaces/${ws.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data?.workspace) {
            // Mark as hydrated before updating to prevent loop
            hydratedWorkspaceIds.current.add(ws.id);
            setCurrentWorkspace(data.workspace);
            localStorage.setItem('selectedWorkspace', JSON.stringify(data.workspace));
          }
        }
      } catch (e) {
        // non-fatal; keep existing workspace
      }
    };
    ensureFullWorkspace();
  }, [currentWorkspace?.id]); // Only depend on ID, not entire object

  

  const selectWorkspace = async (workspace) => {
    try {
      // Use AuthContext to set tenant context
      const result = await setTenantContext(workspace.id, workspace.environment);
      
      if (result.success) {
        // Optionally fetch full workspace to include config like timezone
        try {
          const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
          const token = result.token || localStorage.getItem('tenantAccessToken') || localStorage.getItem('accessToken');
          const resp = await fetch(`${API_BASE_URL}/api/workspaces/${workspace.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (resp.ok) {
            const data = await resp.json();
            const full = data?.workspace || workspace;
            setCurrentWorkspace(full);
            localStorage.setItem('selectedWorkspace', JSON.stringify(full));
          } else {
            setCurrentWorkspace(workspace);
            localStorage.setItem('selectedWorkspace', JSON.stringify(workspace));
          }
        } catch (e) {
          setCurrentWorkspace(workspace);
          localStorage.setItem('selectedWorkspace', JSON.stringify(workspace));
        }
        
        setIsTenantContextReady(true);
        return { success: true };
      } else {
        throw new Error(result.error || 'Failed to set tenant context');
      }
    } catch (error) {
      // Do not persist a selected workspace when the tenant-scoped token could not be issued.
      setCurrentWorkspace(null);
      localStorage.removeItem('selectedWorkspace');
      localStorage.removeItem('tenantAccessToken');
      return { success: false, error: error.message };
    }
  };

  const refreshCurrentWorkspace = () => {
    try {
      const savedWorkspace = localStorage.getItem('selectedWorkspace');
      if (savedWorkspace) {
        const workspace = JSON.parse(savedWorkspace);
        setCurrentWorkspace(workspace);
        return true;
      }
    } catch (error) {
      localStorage.removeItem('selectedWorkspace');
    }
    return false;
  };

  const clearWorkspace = () => {
    setCurrentWorkspace(null);
    localStorage.removeItem('selectedWorkspace');
  };

  const getWorkspaceId = () => currentWorkspace?.id;
  const getWorkspaceName = () => currentWorkspace?.name;
  const getWorkspaceEnvironment = () => currentWorkspace?.environment;

  const value = {
    currentWorkspace,
    workspaces,
    isLoading,
    error,
    selectWorkspace,
    clearWorkspace,
    refreshCurrentWorkspace,
    getWorkspaceId,
    getWorkspaceName,
    getWorkspaceEnvironment,
    isWorkspaceSelected: !!currentWorkspace,
    isTenantContextReady,
    refreshWorkspaces: fetchWorkspaces
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};
